import { useEffect, useRef, useState, memo } from "react";
import { Grid } from "@mui/material";

import { ModelCard as ModelCardComponent, Form } from "../tctt.js";
import AreYouSurePopup from "./Popups/AreYouSurePopup.js";
import Popup from "./Popup.js";
import CreateMission from "./create-mission/index.js";
import CreateMap from "./create-map/index.js";
import Testbed from "../screens/Testbed.js";
import {
	removeModelFromMarketplace,
	cloneModelFromStore,
	newProject,
	addModelToProject,
	getModel,
} from "../api/index.js";
import removeFromMarketplaceIcon from "../assets/dsl-icons/Remove_marketplace.png";
import { theme } from "../theme.js";

const ModelCard = ({
	model,
	setIsLoading,
	success,
	error,
	fetchData,
	textualDsls,
	graphicalDsls,
	projects,
	fromProject = false,
	publicProjects,
	dslImage,
	fromProjectScreen = false,
	fromDslScreen = false,
	removeFromOtherScreen,
	viewFromOtherScreen,
	cloneFromOtherScreen,
	supportsValidation,
}) => {
	const [removePopupOpen, setRemovePopupOpen] = useState(false);
	const [cloneModelPopupOpen, setCloneModelPopupOpen] = useState(false);
	const [viewPopupOpen, setViewPopupOpen] = useState(false);
	const [popupModel, setPopupModel] = useState(null);
	const modelRef = useRef(null);

	const {
		_id: id,
		title,
		model_type,
		description,
		createdAt,
		isUser,
		creator,
		shopEditable,
		model_text,
		creator_fullname,
		validated,
	} = model;

	// Determine if this is a textual DSL
	const normalizedModelType = model_type?.toLowerCase?.().replaceAll?.('-', "") || "";
	const isTextualDsl = textualDsls
		.map((item) => item.toLowerCase().replaceAll('-', ""))
		.includes(normalizedModelType);
	const isGraphicalDsl = graphicalDsls
		.map((item) => item.toLowerCase().replaceAll('-', ""))
		.includes(normalizedModelType);

	// Form content for clone modal
	const newModelFormContent = [
		{
			customType: "input",
			id: "title",
			type: "input",
			multiline: false,
			width: 400,
			placeholder: "Cloned model title",
			required: true,
		},
		{
			customType: "input",
			id: "description",
			type: "input",
			multiline: true,
			minRows: 3,
			width: 400,
			placeholder: "Cloned model description",
		},
		{
			customType: "autocomplete",
			id: "project",
			options: projects ? projects.map((project) => project?.title) : [],
			multiple: true,
			label: "Select project",
			width: 400,
			filled: false,
		},
		{
			customType: "button",
			id: "submit",
			type: "submit",
			text: "Clone model",
		},
	];

	// Handle clone model
	const handleCloneModel = async (values) => {
		setIsLoading(true);
		if (values?.project !== "" || !values?.project) {
			const projectExistance = projects.find((project) => project?.title === values?.project);
			const { success: scs, message, newModel } = await cloneModelFromStore(model._id, values.title, values.description);
			if (scs) {
				if (projectExistance) {
					const { success: scs2, message: msg2 } = await addModelToProject(projectExistance._id, newModel._id);
					if (scs2) {
						success("Model cloned successfully");
						const newTabUrl = `${window.location.origin}/dsls/${model_type}`;
						window.open(newTabUrl, '_blank');
					} else {
						error(msg2);
					}
				} else {
					const { success: scs2, message: msg2, result } = await newProject(values?.project);
					if (scs2) {
						const { success: scs3, message: msg3 } = await addModelToProject(result._id, newModel._id);
						if (scs3) {
							success("Model cloned successfully");
							const newTabUrl = `${window.location.origin}/dsls/${model_type}`;
							window.open(newTabUrl, '_blank');
						} else {
							error(msg3);
						}
					} else {
						error(msg2);
					}
				}
			} else {
				error(message);
			}
		} else {
			const { success: scs, message } = await cloneModelFromStore(model._id, values.title, values.description);
			if (scs) {
				success(message);
				const newTabUrl = `${window.location.origin}/dsls/${model_type}`;
				window.open(newTabUrl, '_blank');
			} else {
				error(message);
			}
		}

		setCloneModelPopupOpen(false);
		setIsLoading(false);
	};

	// Handle remove from marketplace
	const handleRemoveModelFromMarketplace = async () => {
		setIsLoading(true);
		const { success: scs, message } = await removeModelFromMarketplace(model._id);
		if (scs) {
			success(message);
		} else {
			error(message);
		}

		await fetchData();
		setRemovePopupOpen(false);
		setIsLoading(false);
	};

	// Fetch model data for view popup
	useEffect(() => {
		(async () => {
			if (viewPopupOpen) {
				const { success: scs, model: mo } = await getModel(id);
				if (scs) {
					setPopupModel(mo);
				}
			}
		})();
	}, [id, viewPopupOpen]);

	// Determine chip label and color
	const getChipConfig = () => {
		if (fromProjectScreen) {
			return { label: null, color: null };
		}
		if (fromDslScreen) {
			const isValidated = validated || !supportsValidation;
			return {
				label: isValidated ? "Validated" : "Not Validated",
				color: isValidated ? "success" : "error",
			};
		}
		return {
			label: shopEditable ? "Editable" : "Non-Editable",
			color: shopEditable ? "success" : "error",
		};
	};

	const chipConfig = getChipConfig();

	// Determine published in project title
	const getPublishedInProjectTitle = () => {
		if (fromProject || fromDslScreen || fromProjectScreen) return null;
		if (!model?.publishedInProject) return null;
		return publicProjects?.find((prj) => prj?.models?.includes(model._id))?.title || "Unknown";
	};

	// Determine button visibility
	const showCloneButton = !fromProject;
	const showRemoveButton = (isUser && !fromProject && !fromProjectScreen && !fromDslScreen && !model?.publishedInProject)
		|| fromProjectScreen
		|| fromDslScreen;

	// Determine remove button style and icon
	const removeButtonStyle = (fromProjectScreen || fromDslScreen) ? "outlined" : "filled";
	const removeIconSrc = (fromProjectScreen || fromDslScreen) ? null : removeFromMarketplaceIcon;

	// Handlers
	const handleView = () => {
		if (viewFromOtherScreen) {
			viewFromOtherScreen();
		} else {
			setViewPopupOpen(true);
		}
	};

	const handleClone = () => {
		if (cloneFromOtherScreen) {
			cloneFromOtherScreen();
		} else {
			setCloneModelPopupOpen(true);
		}
	};

	const handleRemove = () => {
		if (removeFromOtherScreen) {
			removeFromOtherScreen(model?._id);
		} else {
			setRemovePopupOpen(true);
		}
	};

	const handleMiddleClickView = () => {
		const baseUrl = window.location.origin;
		const modelUrl = `${baseUrl}/dsls/${model_type}/${model?._id}`;
		window.open(modelUrl, "_blank");
	};

	// Render graphical DSL content for view popup
	const renderViewPopupContent = () => {
		if (!viewPopupOpen) return null;

		if (isTextualDsl) {
			return null; // Let the remote component handle textual display
		}

		if (isGraphicalDsl) {
			return (
				<Grid container sx={{ height: "700px" }}>
					{normalizedModelType === "envpop" && (
						<CreateMission ref={modelRef} inPopup model={popupModel?.model_text} dbitem={popupModel} />
					)}
					{normalizedModelType === "envmaker" && (
						<CreateMap inPopup model={popupModel?.model_text} />
					)}
					{normalizedModelType === "appcreator" && (
						<Testbed
							previewMode
							model={model_text}
							dbitem={model}
							modelid={id}
							userid={creator?._id}
							inSimulation={false}
							validationErrors={[]}
						/>
					)}
				</Grid>
			);
		}

		return null;
	};

	// Determine if we should use external popup control (when we have internal view popup)
	const useInternalPopup = !viewFromOtherScreen;

	return (
		<>
			{/* Remove Confirmation Popup */}
			<AreYouSurePopup
				open={removePopupOpen}
				title="Remove model from Marketplace?"
				content="Are you sure you want to remove this model from Marketplace?"
				onDecline={() => setRemovePopupOpen(false)}
				onAccept={handleRemoveModelFromMarketplace}
			/>

			{/* Clone Model Popup */}
			<Popup
				width="800px"
				open={cloneModelPopupOpen}
				title="Clone Model"
				titleColor="#0D192B"
				titleBackgroundColor="#ABC7D9"
				backgroundColor="#DDE8EF"
				closeIconColor="#0D192B"
				onClose={() => setCloneModelPopupOpen(false)}
			>
				<Form
					content={newModelFormContent}
					onSubmit={handleCloneModel}
				/>
			</Popup>

			{/* Model Card Component */}
			<ModelCardComponent
				// Model data
				title={title}
				description={description}
				createdAt={createdAt}
				creatorName={creator_fullname || creator?.fullname}
				modelText={model_text}
				isTextual={isTextualDsl}

				// Display options
				dslImage={dslImage}
				chipLabel={chipConfig.label}
				chipColor={chipConfig.color}
				showCloneButton={showCloneButton}
				showRemoveButton={showRemoveButton}
				showViewButton
				publishedInProjectTitle={getPublishedInProjectTitle()}

				// Handlers
				onView={handleView}
				onClone={showCloneButton ? handleClone : null}
				onRemove={showRemoveButton ? handleRemove : null}
				onMiddleClickView={handleMiddleClickView}

				// View popup - control externally when using internal popup
				{...(useInternalPopup ? {
					viewPopupContent: renderViewPopupContent(),
					viewPopupOpen: viewPopupOpen,
					onViewPopupClose: () => setViewPopupOpen(false),
				} : {})}

				// Remove button customization
				removeIcon={removeIconSrc}
				removeButtonStyle={removeButtonStyle}

				// Theme
				customTheme={theme}
			/>
		</>
	);
};

export default memo(ModelCard);